// Copyright 2022 - 2023 Fewcha. All rights reserved.

import { useAppDispatch, useAppSelector } from "app/hooks";
import APT from "assets/images/svg/aptos.svg";
import Button from "components/Common/Button";
import { selectConfig } from "features/config/configReducer";
import { TokenType } from "features/config/types";
import { selectEvent } from "features/event/eventReducer";
import { selectStats } from "features/stats/statsReducer";
import { connect } from "features/wallet/connect";
import { mint, selectWallet } from "features/wallet/walletReducer";
import React from "react";
import cn from "services/cn";
import { decimalFormat, myToast } from "services/utils";
import styled from "styled-components";

const Card = styled.div`
  position: relative;
  &::before {
    content: "";
    background: linear-gradient(rgba(255, 255, 255, 0) 0%, #ffffff 50.52%, rgba(255, 255, 255, 0) 100%);
    display: inline-block;
    height: 100%;
    position: absolute;
    left: -1px;
    top: 50%;
    width: 2px;
    transform: translate(0, -50%);
  }
  &::after {
    content: "";
    background: linear-gradient(rgba(255, 255, 255, 0) 0%, #ffffff 50.52%, rgba(255, 255, 255, 0) 100%);
    width: 2px;
    display: inline-block;
    height: 100%;
    position: absolute;
    right: -1px;
    top: 50%;
    transform: translate(0, -50%);
  }
`;

const CardImage = styled.div`
  position: relative;
  &::after {
    content: "";
    background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 50.52%, rgba(255, 255, 255, 0) 100%);
    display: inline-block;
    position: absolute;
    height: 2px;
    width: 100%;
    bottom: -18px;
    left: 50%;
    transform: translate(-50%, 0);
    @media (min-width: 1024px) {
      width: 2px;
      height: 100%;
      background: linear-gradient(rgba(255, 255, 255, 0) 0%, #ffffff 50.52%, rgba(255, 255, 255, 0) 100%);
      right: -18px;
      top: 50%;
      left: auto;
      transform: translate(0, -50%);
    }
  }
`;

const Tier = styled.div`
  color: rgba(255, 255, 255, 0.6);
`;

const Tokens: React.FC = () => {
  const dispatch = useAppDispatch();

  const event = useAppSelector(selectEvent);
  const wallet = useAppSelector(selectWallet);
  const config = useAppSelector(selectConfig);
  const stats = useAppSelector(selectStats);

  const submit = async (token: TokenType, index: number) => {
    if (wallet.minting) return;

    if (stats.remainingSupply[index] <= 0) {
      myToast("This NFT sold out!", "warning");
      return;
    }

    if (!wallet.isConnected) {
      dispatch(connect(token));
    } else {
      dispatch(
        mint({
          token,
          address: wallet.address,
          publicKey: wallet.publicKey,
          validNetwork: wallet.validNetwork,
        }),
      );
    }
  };

  const buttonLabel = (index: number) => {
    if (wallet.loading) {
      return "Connecting";
    }

    if (wallet.minting) {
      return "Getting";
    }

    if (!event.isStarted) {
      return "Upcoming";
    }

    if (event.isEnded) {
      return "All Claimed";
    }

    if (stats.remainingSupply[index] <= 0) {
      return "All Claimed";
    }

    return "Get this Perk";
  };

  return (
    <section>
      <div className="mt-11 grid grid-cols-1 md:grid-cols-2 max-w-[80%] lg:max-w-[1024px] mx-auto gap-11">
        {config.tokens.map((token, index) => (
          <Card
            key={index}
            className="fewcha--card rounded-[24px] flex flex-col lg:flex-row p-9 text-white gap-12"
            title={`Tier ${token.tier}`}
          >
            <CardImage className="flex flex-col items-center text-center lg:max-w-[175px] flex-1 justify-between">
              <div className="mb-9">
                <Tier className="text-[16px]">Tier {index + 1}</Tier>
                <p className="text-[20px]">{token.name.slice(0, 7)}</p>
                <p className="text-[20px]">{token.name.slice(8)}</p>
              </div>
              <img
                src={token.image}
                alt={`NFT Tier ${token.tier}`}
                title={`NFT Tier ${token.tier}`}
                className="max-w-[135px]"
              />
            </CardImage>

            <div className="flex-1">
              <div className="text-[26px] text-center font-bold leading-[27px]">
                {/* {event.isStarted && ( */}
                <div className="flex items-center gap-1 justify-center">
                  <img src={APT} alt="APT" /> {decimalFormat(stats.price[index]).toString()} APT
                </div>
                {/* )} */}
              </div>

              <div className="text-center mt-[3px]">
                <span className="font-bold">
                  {stats.totalSupply[index] > stats.maxSupply[index]
                    ? stats.maxSupply[index]
                    : stats.totalSupply[index]}
                  /{stats.maxSupply[index]}
                </span>{" "}
                claimed
              </div>

              <div className="text-[16px] font-light mt-9">
                <ul className="list-inside leading-[19.5px]">
                  {token.description &&
                    token.description.map((text, index) => (
                      <li key={index}>
                        <span className="text-[30px]">•</span> {text}
                      </li>
                    ))}
                </ul>
              </div>

              <Button
                block
                disabled={stats.remainingSupply[index] <= 0 || !event.isStarted || event.isEnded}
                size="large"
                loading={wallet.minting || wallet.loading}
                onClick={() => submit(token, index)}
                className={cn("mt-9", {
                  hidden: event.paused,
                })}
              >
                {buttonLabel(index)}
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default Tokens;
